import appToast from '../../utilities/appToast';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Form, Input, Button, Alert, Spin, Space } from 'antd';
import { useDispatch } from 'react-redux';
import http from '../../store/services/http-common';
import useTranslation from '../../localization/useTranslation';
import { setLanguage } from '../../store/Language/LanguageSlice';

const Invitation = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t, language } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState(null);
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    // Validate token on mount
    const validateToken = async () => {
      if (!token) {
        setTokenError(t('users.invitation.invalidToken') || 'Invalid invitation token');
        setLoading(false);
        return;
      }

      try {
        setValidating(true);
        const response = await http.get(`/invite/${token}`);
        
        if (response.data?.data?.valid) {
          setTokenValid(true);
          setUserInfo(response.data.data.user);
        } else {
          setTokenError(response.data?.message || t('users.invitation.invalidToken') || 'Invalid invitation token');
        }
      } catch (error) {
        const errorMessage = error?.response?.data?.message || t('users.invitation.invalidToken') || 'Invalid invitation token';
        setTokenError(errorMessage);
      } finally {
        setLoading(false);
        setValidating(false);
      }
    };

    validateToken();
  }, [token, t]);

  const onFinish = async (values) => {
    if (!tokenValid || !token) {
      appToast.error(t('users.invitation.invalidToken') || 'Invalid invitation token');
      return;
    }

    try {
      setSubmitting(true);
      const response = await http.post(`/invite/${token}/complete`, {
        password: values.password,
        password_confirmation: values.password_confirmation,
      });

      if (response.status === 200) {
        appToast.success(t('users.invitation.completed') || 'Account verified successfully! You can now log in.');
        // Redirect to login after a short delay
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || t('users.invitation.completeFailed') || 'Failed to complete invitation';
      appToast.error(errorMessage);
      
      if (error?.response?.data?.errors) {
        // Display validation errors
        const errors = error.response.data.errors;
        Object.keys(errors).forEach((field) => {
          form.setFields([
            {
              name: field,
              errors: errors[field],
            },
          ]);
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const languageToggle = (
    <Space size={8}>
      <Button
        size="small"
        type={language === 'en' ? 'primary' : 'default'}
        onClick={() => dispatch(setLanguage('en'))}
      >
        EN
      </Button>
      <Button
        size="small"
        type={language === 'ar' ? 'primary' : 'default'}
        onClick={() => dispatch(setLanguage('ar'))}
      >
        AR
      </Button>
    </Space>
  );

  if (loading || validating) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!tokenValid || tokenError) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: '20px' }} dir={language === 'ar' ? 'rtl' : 'ltr'}>
        <Card
          style={{ maxWidth: 500, width: '100%' }}
          title={t('users.invitation.title') || 'Invitation'}
          extra={languageToggle}
        >
          <Alert
            message={t('users.invitation.error') || 'Error'}
            description={tokenError || t('users.invitation.invalidToken') || 'Invalid or expired invitation token'}
            type="error"
            showIcon
          />
          <Button
            type="primary"
            block
            style={{ marginTop: 16 }}
            onClick={() => navigate('/')}
          >
            {t('users.invitation.backToLogin') || 'Back to Login'}
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: '20px' }} dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <Card
        style={{ maxWidth: 500, width: '100%' }}
        title={t('users.invitation.setPassword') || 'Set Your Password'}
        extra={languageToggle}
      >
        {userInfo && (
          <Alert
            message={t('users.invitation.welcome') || 'Welcome!'}
            description={t('users.invitation.welcomeMessage', { name: userInfo.name, email: userInfo.email }) || `Please set a password for ${userInfo.email}`}
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          autoComplete="off"
        >
          <Form.Item
            name="password"
            label={t('users.form.password.label') || 'Password'}
            rules={[
              { required: true, message: t('users.form.password.required') || 'Please enter a password' },
              { min: 8, message: t('users.form.password.minLength') || 'Password must be at least 8 characters' },
            ]}
          >
            <Input.Password
              placeholder={t('users.form.password.placeholder') || 'Enter your password'}
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item
            name="password_confirmation"
            label={t('users.form.password.confirm') || 'Confirm Password'}
            dependencies={['password']}
            rules={[
              { required: true, message: t('users.form.password.confirmRequired') || 'Please confirm your password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('users.form.password.mismatch') || 'Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password
              placeholder={t('users.form.password.confirmPlaceholder') || 'Confirm your password'}
              autoComplete="new-password"
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={submitting}>
              {t('users.invitation.complete') || 'Complete Setup'}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};

export default Invitation;
